import axios from 'axios';
import {
  usePagesContext,
  useUserContext,
} from '@osmosis/nextjs-components/context';
import { useFetch } from '@osmosis/nextjs-components/context/FetchProvider';
import {
  FeatureFlagsSet,
  useFeatureFlagsContext,
} from '@osmosis/nextjs-components/context/FeatureFlagsContext';
import { UserProductComponentType } from '@osmosis/shared/types/user';
import { track } from '../lib/util/tracking';
import {
  OsmosisAIError,
  OsmosisAIFlow,
  getErrorMetadata,
  withRetry,
} from '../lib/util/osmosisAiRetry';
import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { primeStatusToString } from '@elsevier-health/osmosis-shared/types/users/prime';

export type OsmosisAIUrlParams = {
  searchQuery?: string;
  isIframe?: boolean;
  pageContext?: string;
  shouldInitFreshChat?: boolean;
};

type OAIContextPayload = {
  contextType: 'OSMOSIS_USER';
  linkBackUrl: string;
  program?: string | null;
  productCode?: string | null;
  productID?: number | null;
  paidStatus: string;
  market: 'b2b' | 'b2c';
  pageContext?: string;
  bookPlatform?: 'CKS' | 'VST';
  includeIframeVideoLink: boolean;
};

type OAIRequestPayload = {
  context: OAIContextPayload;
  query: string | undefined;
  isIframe: boolean;
  landOnNewChat?: boolean;
};

const inlineStyle = `
.loader {
    width: 3rem;
    height: .375rem;
    background-color: #ff8200;
    display: inline-block;
    box-sizing: border-box;
    animation: loading 1.6s ease-in-out infinite;
    animation-direction: alternate;
    left: calc(50% - 3.375rem);
    position: absolute;
    top: calc(50% - .5625rem);
    }

    @keyframes loading {
      0% {
          opacity: 0;
          transform: translate(1.875rem);
      }
      40% {
          opacity: .66;
          transform: translate(.625rem);
      }
      60% {
          opacity: .33;
          transform: translate(1.875rem);
      }
    }
`;

const inlineSpinner = `
<style>${inlineStyle}</style>
<span class="loader"></span>
`;

/**
 * Provides the openInNewTab action for launching Osmosis AI in a new browser window.
 * Handles the token exchange with the EOLS API and navigates the new window to the chat.
 */
export default function useOsmosisAI(showLoggedOut?: boolean) {
  const { user, hasPrime, hasExpiredPrime, isUserLoaded, hasProductComponent } =
    useUserContext();
  const { pathname } = usePagesContext();
  const { get } = useFetch();
  const { isActive } = useFeatureFlagsContext();

  const { data: aiFlagState } = useQuery(
    ['osmosis-ai-flag', hasPrime],
    async () => {
      if (!user) return false;
      const { data } = await get<{ state: boolean }>(
        '/api/feature-override?flag=osmosis-ai',
      );
      return data?.state;
    },
  );

  const isOsmosisAiCKSEnabled = isActive(FeatureFlagsSet.osmosisAiCKS);

  const gaFlagState = isActive(FeatureFlagsSet.osmosisAiGa);
  const hasOsmosisAiComponent = hasProductComponent(
    UserProductComponentType.OSMOSIS_AI,
  );

  // Users with the product switcher enabled should have OAI
  // visibility determined solely by the currently selected product.
  const isAIAvailable = Boolean(
    (user?.hasMultiProducts
      ? hasOsmosisAiComponent
      : (gaFlagState && hasOsmosisAiComponent) || (aiFlagState && hasPrime)) &&
      isUserLoaded,
  );

  const isLoggedOut = isUserLoaded && !user;

  const loggedOutShouldShow =
    isUserLoaded && !user && !!gaFlagState && showLoggedOut;

  //  At the moment show lockout modal for expired users only
  const shouldShowLockout = isUserLoaded && hasExpiredPrime && !isLoggedOut;

  const showAIButton = Boolean(
    (isAIAvailable || loggedOutShouldShow) && isUserLoaded,
  );

  const fetchRedirectUrl = useCallback(
    async ({
      searchQuery,
      isIframe,
      pageContext,
      shouldInitFreshChat = true,
      flow = 'new-tab',
    }: OsmosisAIUrlParams & { flow?: OsmosisAIFlow }): Promise<string> => {
      const res = await withRetry(
        () => axios.get<{ token: string; eolsURL: string }>('/api/ai/getToken'),
        { step: 'getToken', flow },
      );

      const token = res?.data?.token;
      const eolsURL = res?.data?.eolsURL;
      if (!token || !eolsURL) {
        throw new OsmosisAIError({
          step: 'getToken',
          flow,
          status: res?.status,
          code: 'NO_TOKEN',
          attempts: 1,
          message: 'No token received from getToken API',
        });
      }

      const reqData: OAIRequestPayload = {
        context: {
          contextType: 'OSMOSIS_USER',
          linkBackUrl: window.location.href,
          program: user?.program,
          productCode: user?.productCode,
          productID: user?.productID,
          paidStatus: primeStatusToString(user?.prime?.status),
          market: user?.prime?.isB2B ? 'b2b' : 'b2c',
          includeIframeVideoLink: true,
        },
        query: searchQuery,
        isIframe: Boolean(isIframe),
      };

      if (pageContext) reqData.context.pageContext = pageContext;
      reqData.landOnNewChat = shouldInitFreshChat;

      if (isOsmosisAiCKSEnabled) {
        const { data } = await withRetry(
          () =>
            axios.get<{ state: boolean }>(
              '/api/feature-override?flag=learn-cks&scope=active-product-cohort',
            ),
          { step: 'featureFlag', flow },
        );
        reqData.context.bookPlatform = data.state ? 'CKS' : 'VST';
      }

      const redirectRes = await withRetry(
        () =>
          axios.post<{ redirectUrl: string }>(
            `${eolsURL}/api/app-link/out/NHE_AI/LAUNCH?includeLinkHash=true&altSrcApp=NHE_AI`,
            reqData,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
        { step: 'launch', flow },
      );

      return redirectRes?.data?.redirectUrl;
    },
    [user, isOsmosisAiCKSEnabled],
  );

  const openInNewTab = useCallback(
    async ({
      shouldInitFreshChat,
      pageContext,
      searchQuery,
    }: OsmosisAIUrlParams = {}) => {
      const newWindow = window.open('', '_blank');

      if (!newWindow) {
        track('osmosis-ai-popup-blocked', { page: pathname }, [
          'keen',
          'pendo',
        ]);
        return;
      }

      newWindow.document.writeln(inlineSpinner);

      try {
        const redirectURL = await fetchRedirectUrl({
          searchQuery,
          pageContext,
          shouldInitFreshChat,
          flow: 'new-tab',
        });
        if (redirectURL) {
          newWindow.location.href = redirectURL;
        }
      } catch (err) {
        newWindow.close();
        track(
          'osmosis-ai-open-error',
          { page: pathname, ...getErrorMetadata(err) },
          ['keen', 'pendo'],
        );
        throw err;
      }
    },
    [pathname, fetchRedirectUrl],
  );

  const getIframeSrc = useCallback(
    async ({
      shouldInitFreshChat,
      pageContext,
    }: OsmosisAIUrlParams = {}): Promise<string> => {
      /**
       * Search queries are ignored in the embedded experience. Its landing view
       * is selected from the presence of page context instead.
       */
      return fetchRedirectUrl({
        isIframe: true,
        pageContext,
        shouldInitFreshChat,
        flow: 'embedded',
      });
    },
    [fetchRedirectUrl],
  );

  return {
    openInNewTab,
    getIframeSrc,
    isAIAvailable,
    loggedOutShouldShow,
    shouldShowLockout,
    isLoggedOut,
    showAIButton,
    isUserLoaded,
  };
}
